<!--
* @Author: AI Assistant
* @Date: 2026-01-12
* @Description: API Discovery 页面 - 核心能力展示与支持模型列表
-->
<template>
    <div class="discovery-page p-4 md:p-6 max-w-[1120px] mx-auto space-y-8 md:space-y-12">
        <h1 class="sr-only">{{ t('api.discoveryH1') }}</h1>
        <!-- 核心能力区域 -->
        <section class="capabilities-section">
            <div class="mb-6">
                <h2 class="text-xl md:text-2xl font-bold text-white mb-2">
                    {{ t('api.coreCapabilities') }}
                </h2>
                <p class="text-sm text-zinc-400">
                    {{ t('api.coreCapabilitiesDesc') }}
                </p>
            </div>

            <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div v-for="capability in capabilities" :key="capability.id"
                    class="capability-card rounded-xl p-4 hover:border-lime-400/50 hover:bg-zinc-900 transition-all cursor-pointer group"
                    @click="handleCapabilityClick(capability)">
                    <div
                        class="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center mb-3 group-hover:bg-lime-400 group-hover:text-black transition-colors">
                        <img v-if="capability.iconImage" :src="capability.iconImage" :alt="capability.name"
                            class="w-5 h-5 object-contain group-hover:brightness-0" />
                        <component v-else :is="capability.iconComponent"
                            class="w-5 h-5 text-lime-400 group-hover:text-black" />
                    </div>
                    <h3 class="text-white font-medium mb-1">
                        {{ capability.name }}
                    </h3>
                    <p class="text-xs text-zinc-500 line-clamp-2 h-8">
                        {{ capability.description }}
                    </p>
                </div>
            </div>
        </section>

        <!-- 支持模型区域 -->
        <section class="models-section">
            <div class="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 class="text-xl md:text-2xl font-bold text-white mb-2">
                        {{ t('api.supportedModels') }}
                    </h2>
                    <p class="text-sm text-zinc-400">
                        {{ t('api.supportedModelsDesc') }}
                    </p>
                </div>
                <div class="flex gap-2">
                    <button class="px-3 py-1 rounded-full text-[10px] md:text-xs font-bold border transition-colors"
                        :class="activeFilter === 'Video'
                                ? 'bg-lime-400/10 text-lime-400 border-lime-400/20'
                                : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-600'
                            " @click="activeFilter = 'Video'">
                        {{ t('api.video') }}
                    </button>
                    <button class="px-3 py-1 rounded-full text-[10px] md:text-xs font-bold border transition-colors"
                        :class="activeFilter === 'Image'
                                ? 'bg-lime-400/10 text-lime-400 border-lime-400/20'
                                : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-600'
                            " @click="activeFilter = 'Image'">
                        {{ t('api.image') }}
                    </button>
                </div>
            </div>

            <!-- 骨架屏 -->
            <div v-if="isLoading" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div v-for="i in 8" :key="i"
                    class="model-card-skeleton flex items-center gap-3 p-3 bg-[#18181b] border border-zinc-800 rounded-lg">
                    <div class="w-10 h-10 rounded bg-zinc-900 flex-shrink-0 border border-zinc-800 skeleton"></div>
                    <div class="min-w-0 flex-1">
                        <div class="h-4 bg-zinc-900 rounded skeleton mb-2"></div>
                        <div class="flex gap-1">
                            <div class="h-5 w-12 bg-zinc-900 rounded skeleton"></div>
                            <div class="h-5 w-16 bg-zinc-900 rounded skeleton"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 模型列表 -->
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div v-for="model in filteredModels" :key="model.id"
                    class="model-card flex items-center gap-3 p-3 bg-[#18181b] border border-zinc-800 rounded-lg hover:border-zinc-600 transition-colors">
                    <div class="w-10 h-10 rounded bg-zinc-900 flex-shrink-0 flex items-center justify-center border border-zinc-800 overflow-hidden"
                        :class="model.type === 'Video'
                                ? 'text-lime-400'
                                : 'text-blue-400'
                            ">
                        <img v-if="model.thumbnail" :src="model.thumbnail" :alt="model.name"
                            class="w-full h-full object-cover" />
                        <span v-else class="text-[10px] font-bold uppercase">
                            {{ model.type === 'Video' ? 'VID' : 'IMG' }}
                        </span>
                    </div>
                    <div class="min-w-0">
                        <h4 class="text-sm font-medium text-white truncate">
                            {{ model.name }}
                        </h4>
                        <div class="flex gap-1 mt-1 flex-wrap">
                            <span v-for="tag in model.tags.slice(0, 2)" :key="tag"
                                class="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-500 border border-zinc-800 whitespace-nowrap">
                                {{ tag }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, h, onMounted } from 'vue'
import { navigateTo, useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

// 使用多语言 SEO
useMultilingualSeo({ pageType: 'apiDiscovery' })


// 导入图标图片
import textToImageIcon from '~/assets/images/api/text-to-image.png'
import imageToImageIcon from '~/assets/images/api/image-to-image.png'
import textToVideoIcon from '~/assets/images/api/text-to-video.png'
import imageToVideoIcon from '~/assets/images/api/image-to-video.png'
import multiImageToVideoIcon from '~/assets/images/api/multi-image-to-video.png'
import aiMusicIcon from '~/assets/images/api/ai-music.png'
import digitalAvatarIcon from '~/assets/images/api/digital-avatar.png'

// ========== 图标组件 ==========
const MicIcon = {
    render: () =>
        h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                width: '20',
                height: '20',
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                'stroke-width': '2',
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
            },
            [
                h('path', { d: 'M12 19v3' }),
                h('path', { d: 'M19 10v2a7 7 0 0 1-14 0v-2' }),
                h('rect', {
                    x: '9',
                    y: '2',
                    width: '6',
                    height: '13',
                    rx: '3',
                }),
            ]
        ),
}

// ========== 类型定义 ==========
interface Capability {
    id: string
    name: string
    description: string
    endpoint: string
    route?: string
    iconComponent?: any
    iconImage?: string
}

interface Model {
    id: string
    name: string
    type: 'Video' | 'Image'
    tags: string[]
    thumbnail?: string
}

interface ApiModel {
    model_key: string
    title: string
    desc: string
    thumbnail?: string
    marks: string[]
    category?: {
        code: string
        title: string
        description: string
    }
    is_new?: boolean
    is_hot?: boolean
}

interface ApiResponse {
    image_models?: {
        text_to_image?: ApiModel[]
        image_to_image?: ApiModel[]
    }
    video_models?: {
        text_to_video?: ApiModel[]
        image_to_video?: ApiModel[]
    }
}

// ========== 能力数据（使用 computed 实现响应式国际化） ==========
const capabilities = computed<Capability[]>(() => [
    {
        id: 'text-to-image',
        name: t('api.textToImage'),
        description: t('api.textToImageDesc'),
        endpoint: '/v1/generation/text-to-image',
        route: '/text-to-image',
        iconImage: textToImageIcon,
    },
    {
        id: 'image-to-image',
        name: t('api.imageToImage'),
        description: t('api.imageToImageDesc'),
        endpoint: '/v1/generation/image-to-image',
        route: '/image-to-image',
        iconImage: imageToImageIcon,
    },
    {
        id: 'text-to-video',
        name: t('api.textToVideo'),
        description: t('api.textToVideoDesc'),
        endpoint: '/v1/generation/text-to-video',
        route: '/text-to-video',
        iconImage: textToVideoIcon,
    },
    {
        id: 'image-to-video',
        name: t('api.imageToVideo'),
        description: t('api.imageToVideoDesc'),
        endpoint: '/v1/generation/image-to-video',
        route: '/image-to-video',
        iconImage: imageToVideoIcon,
    },
    {
        id: 'multi-image-to-video',
        name: t('api.multiImageToVideo'),
        description: t('api.multiImageToVideoDesc'),
        endpoint: '/v1/generation/multi-image-to-video',
        route: '/image-to-video',
        iconImage: multiImageToVideoIcon,
    },
    {
        id: 'podcast',
        name: t('api.podcast'),
        description: t('api.podcastDesc'),
        endpoint: '/v1/tools/podcast',
        iconComponent: MicIcon,
    },
    {
        id: 'music',
        name: t('api.music'),
        description: t('api.musicDesc'),
        endpoint: '/v1/tools/music',
        route: '/ai-music',
        iconImage: aiMusicIcon,
    },
    {
        id: 'digital_avatar',
        name: t('api.digitalAvatar'),
        description: t('api.digitalAvatarDesc'),
        endpoint: '/v1/tools/digital_avatar_video',
        route: '/characters',
        iconImage: digitalAvatarIcon,
    },
])

// ========== 点击处理 ==========
const handleCapabilityClick = (capability: Capability) => {
    if (capability.route) {
        // 如果是多图生视频，添加 query 参数来指定 tab
        if (capability.id === 'multi-image-to-video') {
            navigateTo({
                path: capability.route,
                query: { tab: 'multi' }
            })
        } else {
            navigateTo(capability.route)
        }
    }
}

const models = ref<Model[]>([])
const isLoading = ref(true)

// ========== 数据转换 ==========
const transformApiModelToModel = (
    apiModel: ApiModel,
    type: 'Video' | 'Image'
): Model => {
    const tags: string[] = []

    // 添加 marks
    if (apiModel.marks && apiModel.marks.length > 0) {
        tags.push(...apiModel.marks)
    }

    // 添加 category title
    if (apiModel.category?.title) {
        tags.push(apiModel.category.title)
    }

    // 添加 is_new 和 is_hot 标记
    if (apiModel.is_new) {
        tags.push('New')
    }
    if (apiModel.is_hot) {
        tags.push('Hot')
    }

    return {
        id: apiModel.model_key,
        name: apiModel.title,
        type,
        tags: tags.length > 0 ? tags : ['Standard'],
        thumbnail: apiModel.thumbnail,
    }
}

// ========== 获取模型列表 ==========
const fetchModels = async () => {
    try {
        isLoading.value = true

        // 从 S3 静态资源服务器读取 JSON 文件
        // 带时间戳用于开发调试，生产环境会通过 HTTP 响应头实现强制缓存
        const response = await fetch('https://static.weryai.com/cms/openapi-model-info.json')

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = (await response.json()) as ApiResponse

        console.log('📦 从 S3 加载模型数据:', {
            url: 'https://static.weryai.com/cms/openapi-model-info.json',
            cacheStatus: response.headers.get('cache-control'),
            etag: response.headers.get('etag'),
            timestamp: new Date().toLocaleTimeString()
        })

        if (data) {
            const { image_models, video_models } = data
            const modelList: Model[] = []

            // 处理图片模型
            if (image_models) {
                if (image_models.text_to_image) {
                    image_models.text_to_image.forEach((model: ApiModel) => {
                        modelList.push(transformApiModelToModel(model, 'Image'))
                    })
                }
                if (image_models.image_to_image) {
                    image_models.image_to_image.forEach((model: ApiModel) => {
                        // 避免重复添加相同的模型
                        if (!modelList.find(m => m.id === model.model_key)) {
                            modelList.push(
                                transformApiModelToModel(model, 'Image')
                            )
                        }
                    })
                }
            }

            // 处理视频模型
            if (video_models) {
                if (video_models.text_to_video) {
                    video_models.text_to_video.forEach((model: ApiModel) => {
                        modelList.push(transformApiModelToModel(model, 'Video'))
                    })
                }
                if (video_models.image_to_video) {
                    video_models.image_to_video.forEach((model: ApiModel) => {
                        // 避免重复添加相同的模型
                        if (!modelList.find(m => m.id === model.model_key)) {
                            modelList.push(
                                transformApiModelToModel(model, 'Video')
                            )
                        }
                    })
                }
            }

            models.value = modelList
            console.log('✅ 模型数据加载成功:', {
                imageModels: modelList.filter(m => m.type === 'Image').length,
                videoModels: modelList.filter(m => m.type === 'Video').length,
                total: modelList.length
            })
        }
    } catch (error) {
        console.error('❌ 加载模型数据失败:', error)
    } finally {
        isLoading.value = false
    }
}

onMounted(() => {
    fetchModels()
})

// ========== 筛选逻辑 ==========
const activeFilter = ref<'Video' | 'Image'>('Video')

const filteredModels = computed(() => {
    return models.value.filter(model => model.type === activeFilter.value)
})


// 结构化数据
useHead({
    script: [
        {
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('api.discovery'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
        {
            key: 'itemlist-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    name: t('api.supportedModels'),
                    description: t('api.supportedModelsDesc'),
                    itemListElement: models?.value?.map((model, index) => ({
                        '@type': 'ListItem',
                        position: index + 1,
                        name: model.name,
                        url: requestUrlHref,
                    })),
                })
            ),
        },
    ],
})
</script>

<style scoped lang="less">
.discovery-page {
    animation: fadeIn 0.3s ease-out;
}

/* Screen reader only */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.capability-card {
    background-color: rgba(28, 28, 32, 1);
    border: 1px solid rgba(255, 255, 255, 0.06);

    &:hover {
        transform: translateY(-2px);
        border-color: rgba(163, 230, 53, 0.5); // lime-400/50
    }
}

.model-card {
    &:hover {
        transform: translateX(2px);
    }
}

// 骨架屏样式
.skeleton {
    background: linear-gradient(90deg,
            rgba(255, 255, 255, 0.05) 25%,
            rgba(255, 255, 255, 0.1) 50%,
            rgba(255, 255, 255, 0.05) 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s ease-in-out infinite;
}

@keyframes skeleton-loading {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

.model-card-skeleton {
    pointer-events: none;
}
</style>
